import React, { createContext, useContext } from 'react';
import { EnvConfigRecord } from '../../types/EnvironmentConfig';
import { AttestationFacade } from './AttestationFacade';
import Api from '../../store/Api';
import StateProgramsError from '../state-programs/errors/StateProgramsError';

/**
 * Registry of dependencies to be used for the current environment.
 * All of these services should be present before the app can be used.
 * (This is inspired by the set of available beans for autowiring in Spring.)
 */
export interface PlentifulServiceRegistry {
  /**
   * Singleton for hitting Attestation Service for the current environment.
   */
  attestationFacade: AttestationFacade;
}

/**
 * Context provider vends services or the error that causes them not to be used.
 * Visible for testing. Avoid using directly.
 * Prefer to use {@link PlentifulServiceContextProvider}
 * and {@link usePlentifulServiceContext} instead.
 */
export const PlentifulServiceContext = createContext<PlentifulServiceRegistry | Error>(
  new StateProgramsError(
    'PlentifulServiceContext must be provided before using context',
    new Error('Context not provided'),
    'UNKNOWN'
  )
);

/**
 * Custom hook to provide {@link PlentifulServiceRegistry} based on context.
 * Wraps built-in hook to ensure that context has been provided.
 */
export const usePlentifulServiceContext = (): PlentifulServiceRegistry => {
  const context = useContext(PlentifulServiceContext);
  if (context instanceof Error) {
    throw context;
  }
  return context;
};

/**
 * Props for providing state program services.
 */
interface PlentifulServiceContextProviderProps {
  /**
   * Environment config to be used for initializing dependencies.
   */
  envVars: EnvConfigRecord;
  /**
   * Optional child nodes that will use the context.
   */
  children?: React.ReactNode;
}

/**
 * Provider to state program services.
 * Wraps built-in provider to ensure that program services
 * can be loaded before being used.
 */
export const PlentifulServiceContextProvider = function (props: PlentifulServiceContextProviderProps) {
  // instantiate each required service
  try {
    const attestationFacade = new AttestationFacade(Api);

    return (
      <PlentifulServiceContext.Provider value={{ attestationFacade }}>
        {props.children}
      </PlentifulServiceContext.Provider>
    );
  } catch (cause) {
    // throw the error immediately and let the global error boundary report it
    throw new StateProgramsError(
      `Plentiful Services failed to initialize given environment: ${JSON.stringify(
        props.envVars,
        undefined,
        2
      )}`,
      cause,
      'UNKNOWN'
    );
  }
};
